import React, { useMemo } from "react"
import { Tile, Stack, Typography, Button, Link } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { useHistory, useLocation } from "react-router-dom"
import { useSendMessagePath } from "#/components/talk-to-carrot/hooks/useSendMessagePath"
import { useExpertBios } from "#/components/care-team/hooks/useCareTeamExperts"
import { ClosingCopy, ExpertBioList } from "#/components/talk-to-carrot/TalkToCarrotExpertsSidebar"
import { Paths, TtcPaths } from "#/utils/Paths"
// TypeCalendlyLinkSkeleton is the TimeTap link type, auto-generated from Contentful
import { TypeCalendlyLinkSkeleton } from "#/types/contentful"
import { mapContentToExpertBio } from "#/components/read/shared/ExpertBioMapper"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { CareTeamExpertWithTiles } from "#/components/care-team/utils/careTeamUtil"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"

const Heading = () => {
  return (
    <Typography variant="h4">
      <FormattedMessage defaultMessage="Don’t see a time that works for you?" />
    </Typography>
  )
}

export const DontSeeATimeThatWorksForYouTile = ({
  timeTapLink
}: {
  timeTapLink: EntryWithoutUnresolvableLinks<TypeCalendlyLinkSkeleton>
}) => {
  const sendMessagePath = useSendMessagePath()
  const history = useHistory()
  const location = useLocation<{ expert: CareTeamExpertWithTiles; reasonId: number }>()
  const currentProfessionalId = location.state?.expert?.expertBio?.professionalId
  const currentReasonId = location.state?.reasonId
  const { mutate: trackAction } = useMemberActionTracking()

  const expertBiosFromTimeTapLink = timeTapLink.fields.expertBios?.map((expertBio) => mapContentToExpertBio(expertBio))
  const filteredExpertBiosFromTimeTapLink = expertBiosFromTimeTapLink?.filter(
    (expertBio) => expertBio.professionalId !== currentProfessionalId
  )

  const allExpertBios = useExpertBios()
  const filteredExpertBios = allExpertBios?.data?.filter(
    (expertBio) =>
      expertBio.timeTapReasons?.some((timeTapReason) => timeTapReason.reasonId === currentReasonId) &&
      expertBio.professionalId !== currentProfessionalId
  )

  const expertBios = useMemo(
    () =>
      Array.from(
        (filteredExpertBiosFromTimeTapLink || [])
          .concat(filteredExpertBios || [])
          .reduce((map, bio) => map.set(bio.id, bio), new Map())
          .values()
      ),
    [filteredExpertBiosFromTimeTapLink, filteredExpertBios]
  )

  const specialistChatsUrl = `${Paths.TALK_TO_CARROT}/${TtcPaths.COMPANY_GEO_PATH_SEGMENT}/${TtcPaths.SPECIALIST_PATH_SEGMENT}`

  return (
    <Tile sx={{ inlineSize: { lg: "19.5rem" }, blockSize: "fit-content" }}>
      <Stack gap={(theme) => theme.tokens.spacing.md}>
        {expertBios && expertBios.length > 0 ? (
          <>
            <Stack gap={(theme) => theme.tokens.spacing.sm} marginBlockEnd={(theme) => theme.tokens.spacing.sm}>
              <Heading />
              <Typography>
                <FormattedMessage defaultMessage="If you can’t find a time with your preferred specialist, you can chat with another expert from a team that includes:" />
              </Typography>
            </Stack>
            <ExpertBioList expertBios={expertBios} analyticsPrefix="care-team-book-again-contact-us-card" />
            <ClosingCopy />
            <Button
              onClick={() => {
                trackAction({
                  memberActionTaken: MemberActions.BOOK_AGAIN_FIND_A_SPECIALIST_BUTTON_CLICKED
                })
                history.push(specialistChatsUrl)
              }}
              color="secondary"
              variant="outlined"
              fullWidth={false}
              data-analytics-id="care-team-book-again-contact-us-card-find-a-specialist-button"
              sx={{ inlineSize: "fit-content" }}
            >
              <FormattedMessage defaultMessage="Find a specialist" />
            </Button>
          </>
        ) : (
          <>
            <Heading />
            <Link
              href={sendMessagePath}
              color="inherit"
              data-analytics-id="care-team-book-again-contact-us-card-contact-link"
            >
              <FormattedMessage defaultMessage="Contact us" />
            </Link>
          </>
        )}
      </Stack>
    </Tile>
  )
}
